import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ColorSpace } from 'editor/src/store/design/types';
import { useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import CMYK from 'editor/src/util/color/cmykUtils';
import { EMPTY_RGBA_STRING } from 'editor/src/util/color/rgbaUtils';

import { parseColorToRGBA } from '../../util/color/colorUtils';
import SplitterHorizontal from '../TopMenuDesktop/buttons/SplitterHorizontal';

import ColorControllers from './ColorControllers';
import colors from './colors';
import Palette from './Palette';
import ResetButton from './ResetButton';

import styles from './index.module.scss';

interface Props {
  color: string | undefined;
  onColorChange: (color: string | undefined, opacity?: number) => void;
  onColorChanging?: (color: string | undefined, opacity?: number) => void;
  allowNoColor?: boolean;
  allowNoColorLabel?: string;
  allowOpacity?: boolean;
  colors?: string[];
  paletteColorsInRowCount?: number;
  hidePickFromPaletteNote?: true;
  suggestedColors?: string[];
  ignoreCMYK?: boolean;
}

function ColorPicker({
  color,
  onColorChanging,
  onColorChange,
  allowNoColor,
  allowOpacity,
  colors: availableColors,
  suggestedColors,
  allowNoColorLabel,
  paletteColorsInRowCount = 8,
  hidePickFromPaletteNote,
  ignoreCMYK,
}: Props) {
  const { t } = useTranslation();
  const colorPickerConfig = useSelector((state) => getHostSetting(state, 'colorPickerConfig'));
  const colorSpace = useSelector((state) => state.design.designData?.color_space) ?? ColorSpace.sRGB;

  const onResetColor = useCallback(() => {
    if (allowNoColor) {
      onColorChange(undefined);
    }
  }, [allowNoColor, onColorChange]);

  const onPaletteColorSelect = useCallback(
    (paletteColor: string) => {
      if (paletteColor === EMPTY_RGBA_STRING) {
        onColorChange(undefined);
        return;
      }
      if (colorSpace === ColorSpace.CMYK) {
        const rgba = parseColorToRGBA(paletteColor);
        onColorChange(CMYK.fromRGB(rgba).toString());
        return;
      }
      onColorChange(paletteColor);
    },
    [colorSpace, onColorChange],
  );

  const noColor = !color || color === EMPTY_RGBA_STRING;

  return (
    <div className="cy-color-picker">
      {allowNoColor && (
        <ResetButton
          className="mb-2 mt-2"
          label={allowNoColorLabel || t('No color')}
          active={noColor}
          onReset={onResetColor}
        />
      )}
      <div className={styles.palette}>
        {!hidePickFromPaletteNote && <span className={styles.label}>{t('editor-pick-from-palette')}</span>}
        <Palette
          colors={availableColors || colors}
          selectedColor={!noColor ? parseColorToRGBA(color).toHex() : undefined}
          onColorSelect={onPaletteColorSelect}
          suggestedColors={suggestedColors}
          colorsInRowCount={paletteColorsInRowCount}
          colorsInfo={colorPickerConfig?.colorsInfo}
          colorSpace={colorSpace}
        />
      </div>
      {!availableColors?.length && (
        <>
          <SplitterHorizontal />
          <ColorControllers
            color={color}
            colorSpace={colorSpace}
            hideRGB={(allowNoColor && noColor) || undefined}
            onColorChange={onColorChange}
            onColorChanging={onColorChanging}
            className={styles.picker}
            label={t('editor-create-custom-color')}
            allowOpacity={!!allowOpacity}
            ignoreCMYK={ignoreCMYK}
          />
        </>
      )}
    </div>
  );
}

export default React.memo(ColorPicker);
